import { Button, Table, TableCell, TableColumnHeader, TableRow } from '@/common'
import useDialog from '@/common/hooks/useDialog'
import { useCallback, useMemo, useState } from 'react'
import useI18n from '@/shared/hooks/useI18n'
import {
  Artifacts,
  FuelItem,
  MaterialOutputItem,
  MaterialInputItem,
  MassBalanceItem
} from '@/api/EmissionReportModel'
import CombustionCalculatorPanel from '../calculators/CombustionCalculatorPanel'
import { useUpdateMethodEmissionReport } from '@/hooks/api/emissionReports'
import { uniqBy } from 'es-toolkit'
import InputTableRow from './InputTableRow'
import GenericMassBalanceItemPanel from './GenericMassBalanceItemPanel'
import EditMaterialOutputDialog from './EditMaterialOutputDialog'
import OutputTableRow from './OutputTableRow'
import { MethodCalculatedEmission } from '@/shared/api/types'
import { UnitLabel } from '@/shared/config/config'

function InputTableRowGroup({
  items,
  title,
  onRemove,
  onEdit,
  isViewMode
}: {
  items: MassBalanceItem[]
  title: string
  onRemove?: (index: number) => void
  onEdit?: (index: number) => void
  isViewMode: boolean
}) {
  return (
    <>
      {items.map((item, index) => (
        <InputTableRow
          key={item.materialName + index}
          id={item.materialName}
          index={index}
          item={item}
          title={title}
          onRemove={onRemove}
          onEdit={onEdit}
          isViewMode={isViewMode}
        />
      ))}
    </>
  )
}

function OutputTableRowGroup({
  title,
  items,
  onEdit
}: {
  title: string
  items: MaterialOutputItem[]
  onEdit: (cnCode: string) => void
}) {
  return (
    <>
      {items.map((item, index) => (
        <OutputTableRow title={title} key={item.cnCode} index={index} item={item} onEdit={onEdit} />
      ))}
    </>
  )
}

function replaceItemInArray<T>(array: T[], index: number, item: T) {
  return array.map((_, i) => (i === index ? item : _))
}

export default function MassBalanceReporting({
  nextMethodEmissionReportId,
  scrapInputItems,
  scrapOutputItems,
  fuelItems,
  otherMaterialsInput,
  otherMaterialsOutput,
  materialOutput: initialMaterialOutput,
  methodEmissionReportId,
  reportId,
  materialInput: initialMaterialInput,
  methodCalculatedEmission,
  isViewMode
}: {
  nextMethodEmissionReportId: string | null
  cnCodes: string[]
  scrapInputItems: MassBalanceItem[]
  scrapOutputItems: MassBalanceItem[]
  fuelItems: FuelItem[]
  materialOutput: MaterialOutputItem[]
  otherMaterialsInput: MassBalanceItem[]
  otherMaterialsOutput: MassBalanceItem[]
  methodEmissionReportId: string
  reportId: string
  materialInput: MaterialInputItem[]
  methodCalculatedEmission: MethodCalculatedEmission
  isViewMode: boolean
}) {
  const { mutateAsync: updateMethodEmissionReport } = useUpdateMethodEmissionReport(
    reportId,
    methodEmissionReportId
  )

  const { mutateAsync: updateNextMethodEmissionReport } = useUpdateMethodEmissionReport(
    reportId,
    nextMethodEmissionReportId
  )

  const {
    open: isEditMaterialOutputDialogOpen,
    openDialog: openEditMaterialOutputDialog,
    closeDialog: closeEditMaterialOutputDialog
  } = useDialog()

  const {
    open: isMassBalanceDialogOpen,
    openDialog: openMassBalanceDialog,
    closeDialog: closeMassBalanceDialog
  } = useDialog()

  const [editedCnCode, setEditedCnCode] = useState<string | null>(null)
  const [editedItemIndex, setEditedItemIndex] = useState<number | null>(null)
  const [editedBucket, setEditedBucket] = useState<string | null>(null)
  const [editedFuelInputIndex, setEditedFuelInputIndex] = useState<number | null>(null)
  const editedMaterialOutputItem = useMemo(() => {
    return initialMaterialOutput.find(item => item.cnCode === editedCnCode)
  }, [editedCnCode, initialMaterialOutput])

  const resetEditedMaterialOutputItem = useCallback(() => {
    setEditedCnCode(null)
    closeEditMaterialOutputDialog()
  }, [closeEditMaterialOutputDialog])

  const initEditProductOutput = useCallback(
    (cnCode: string) => {
      setEditedCnCode(cnCode)
      openEditMaterialOutputDialog()
    },
    [openEditMaterialOutputDialog]
  )

  const { t, numberFormatter3Decimals } = useI18n()

  const {
    open: isCombustionDialogOpen,
    openDialog: openCombustionDialog,
    closeDialog: closeCombustionDialog
  } = useDialog()

  const materialOutput = initialMaterialOutput

  const onRemoveMassBalanceItem = useCallback(
    async (bucket: string, index: number) => {
      switch (bucket) {
        case 'scrapInput':
          const updateScrapInput = scrapInputItems.filter((_, i) => i !== index)
          await updateMethodEmissionReport({
            scrapInput: updateScrapInput
          })
          break
        case 'otherMaterialsInput':
          const updateOtherMaterialsInput = otherMaterialsInput.filter((_, i) => i !== index)
          await updateMethodEmissionReport({
            otherMaterialsInput: updateOtherMaterialsInput
          })
          break
        case 'scrapOutput':
          const updateScrapOutput = scrapOutputItems.filter((_, i) => i !== index)
          await updateMethodEmissionReport({
            scrapOutput: updateScrapOutput
          })
          break
        case 'otherMaterialsOutput':
          const updateOtherMaterialsOutput = otherMaterialsOutput.filter((_, i) => i !== index)
          await updateMethodEmissionReport({
            otherMaterialsOutput: updateOtherMaterialsOutput
          })
          break
      }
    },
    [
      scrapInputItems,
      otherMaterialsInput,
      otherMaterialsOutput,
      scrapOutputItems,
      updateMethodEmissionReport
    ]
  )

  const onAddCombustion = useCallback(
    async (emission: number, artifacts: Artifacts) => {
      let updateFuelInput: FuelItem[] = fuelItems
      const isEditing = editedFuelInputIndex || editedFuelInputIndex === 0
      if (isEditing) {
        updateFuelInput = replaceItemInArray(fuelItems, editedFuelInputIndex, {
          fuelType: artifacts.fuelType,
          quantity: Number(artifacts.fuelConsumed),
          emission,
          artifacts
        })
        setEditedFuelInputIndex(null)
      } else {
        updateFuelInput = [
          ...fuelItems,
          {
            fuelType: artifacts.fuelType,
            quantity: Number(artifacts.fuelConsumed),
            emission,
            artifacts
          }
        ]
      }
      await updateMethodEmissionReport({
        fuelInput: updateFuelInput
      })
      closeCombustionDialog()
    },
    [editedFuelInputIndex, fuelItems, updateMethodEmissionReport, closeCombustionDialog]
  )

  const onEditMaterialOutput = useCallback(
    async (
      cnCode: string,
      carbonContent: {
        quantity: number
        carbonContent: number
        emission: number
      }
    ) => {
      const updatedMaterialOutput = uniqBy(materialOutput, item => item.cnCode).map(item => {
        if (item.cnCode === cnCode) {
          return { ...item, carbonContent }
        }
        return item
      })

      await updateMethodEmissionReport({
        materialOutput: updatedMaterialOutput
      })

      // if there is a next method, update the next method's material input
      if (nextMethodEmissionReportId) {
        await updateNextMethodEmissionReport({
          materialInput: updatedMaterialOutput
        })
      }
      resetEditedMaterialOutputItem()
    },
    [
      nextMethodEmissionReportId,
      updateMethodEmissionReport,
      updateNextMethodEmissionReport,
      materialOutput,
      resetEditedMaterialOutputItem
    ]
  )

  const onRemoveFuelInput = useCallback(
    async (index: number) => {
      const updateFuelInput = fuelItems.filter((_, i) => i !== index)
      await updateMethodEmissionReport({
        fuelInput: updateFuelInput
      })
    },
    [fuelItems, updateMethodEmissionReport]
  )

  const formattedBalance = useMemo(() => {
    return `${numberFormatter3Decimals(methodCalculatedEmission?.massBalance || 0)}`
  }, [methodCalculatedEmission?.massBalance, numberFormatter3Decimals])

  const initMassBalanceItemEdit = useCallback(
    (bucket: string, index: number) => {
      setEditedBucket(bucket)
      switch (bucket) {
        case 'scrapInput':
          setEditedItemIndex(index)
          break
        case 'scrapOutput':
          setEditedItemIndex(index)
          break
        case 'otherMaterialsInput':
          setEditedItemIndex(index)
          break
        case 'otherMaterialsOutput':
          setEditedItemIndex(index)
          break
      }
      openMassBalanceDialog()
    },
    [openMassBalanceDialog]
  )

  const onAddGeneric = useCallback(
    (bucket: string) => {
      setEditedBucket(bucket)
      setEditedItemIndex(null)
      openMassBalanceDialog()
    },
    [openMassBalanceDialog]
  )

  const onFinishEditing = useCallback(
    async (item: MassBalanceItem) => {
      const isEditing = editedItemIndex !== null
      if (editedBucket === 'otherMaterialsOutput') {
        await updateMethodEmissionReport({
          otherMaterialsOutput: isEditing
            ? replaceItemInArray(otherMaterialsOutput, editedItemIndex, item)
            : [...otherMaterialsOutput, item]
        })
      } else if (editedBucket === 'otherMaterialsInput') {
        await updateMethodEmissionReport({
          otherMaterialsInput: isEditing
            ? replaceItemInArray(otherMaterialsInput, editedItemIndex, item)
            : [...otherMaterialsInput, item]
        })
      } else if (editedBucket === 'scrapInput') {
        await updateMethodEmissionReport({
          scrapInput: isEditing
            ? replaceItemInArray(scrapInputItems, editedItemIndex, item)
            : [...scrapInputItems, item]
        })
      } else if (editedBucket === 'scrapOutput') {
        await updateMethodEmissionReport({
          scrapOutput: isEditing
            ? replaceItemInArray(scrapOutputItems, editedItemIndex, item)
            : [...scrapOutputItems, item]
        })
      }
      closeMassBalanceDialog()
    },
    [
      editedBucket,
      editedItemIndex,
      otherMaterialsOutput,
      otherMaterialsInput,
      scrapInputItems,
      scrapOutputItems,
      updateMethodEmissionReport,
      closeMassBalanceDialog
    ]
  )

  const onFuelInputEdit = useCallback(
    (index: number) => {
      setEditedFuelInputIndex(index)
      openCombustionDialog()
    },
    [openCombustionDialog]
  )

  const editedItem = useMemo(() => {
    if (editedItemIndex === null) {
      return null
    }
    switch (editedBucket) {
      case 'otherMaterialsOutput':
        return otherMaterialsOutput[editedItemIndex]
      case 'otherMaterialsInput':
        return otherMaterialsInput[editedItemIndex]
      case 'scrapInput':
        return scrapInputItems[editedItemIndex]
      case 'scrapOutput':
        return scrapOutputItems[editedItemIndex]
    }
  }, [
    editedBucket,
    otherMaterialsOutput,
    otherMaterialsInput,
    scrapInputItems,
    scrapOutputItems,
    editedItemIndex
  ])

  const editedFuelInputItem = useMemo(() => {
    if (editedFuelInputIndex === null) {
      return null
    }
    return fuelItems[editedFuelInputIndex]
  }, [editedFuelInputIndex, fuelItems])

  return (
    <>
      <Table borderless dense className="mb-4">
        <thead>
          <TableRow>
            <TableColumnHeader className="w-40" />
            <TableColumnHeader>
              {t('report:massBalance.table.headers.materialName')}
            </TableColumnHeader>
            <TableColumnHeader>
              {t('report:massBalance.table.headers.description')}
            </TableColumnHeader>
            <TableColumnHeader>
              {t('report:massBalance.table.headers.quantity')} ({UnitLabel.t})
            </TableColumnHeader>
            <TableColumnHeader>
              {t('report:massBalance.table.headers.emission')} ({UnitLabel.tCO2})
            </TableColumnHeader>
            <TableColumnHeader />
          </TableRow>
        </thead>
        <tbody>
          <InputTableRowGroup
            isViewMode={isViewMode}
            title={t('report:massBalance.table.balanceItems.materialInput')}
            items={initialMaterialInput.map(item => ({
              materialName: item.cnCode,
              description: '-',
              carbonContent: item.carbonContent.carbonContent,
              quantity: item.carbonContent.quantity,
              emission: item.carbonContent.emission
            }))}
          />
          <InputTableRowGroup
            isViewMode={isViewMode}
            title={t('report:massBalance.table.balanceItems.scrapInput')}
            items={scrapInputItems}
            onRemove={index => onRemoveMassBalanceItem('scrapInput', index)}
            onEdit={index => initMassBalanceItemEdit('scrapInput', index)}
          />
          <InputTableRowGroup
            title={t('report:massBalance.table.balanceItems.fuelInput')}
            items={fuelItems.map(item => ({
              materialName: t(`common:fuelTypes.${item.fuelType}`),
              description: item.description,
              quantity: item.quantity,
              carbonContent: 0,
              emission: item.emission
            }))}
            isViewMode={isViewMode}
            onRemove={onRemoveFuelInput}
            onEdit={index => onFuelInputEdit(index)}
          />
          <InputTableRowGroup
            isViewMode={isViewMode}
            title={t('report:massBalance.table.balanceItems.otherMaterialsInput')}
            items={otherMaterialsInput}
            onRemove={index => onRemoveMassBalanceItem('otherMaterialsInput', index)}
            onEdit={index => initMassBalanceItemEdit('otherMaterialsInput', index)}
          />
          <InputTableRowGroup
            isViewMode={isViewMode}
            title={t('report:massBalance.table.balanceItems.scrapOutput')}
            items={scrapOutputItems}
            onRemove={index => onRemoveMassBalanceItem('scrapOutput', index)}
            onEdit={index => initMassBalanceItemEdit('scrapOutput', index)}
          />
          <InputTableRowGroup
            isViewMode={isViewMode}
            title={t('report:massBalance.table.balanceItems.otherMaterialsOutput')}
            items={otherMaterialsOutput}
            onRemove={index => onRemoveMassBalanceItem('otherMaterialsOutput', index)}
            onEdit={index => initMassBalanceItemEdit('otherMaterialsOutput', index)}
          />
          <OutputTableRowGroup
            title={t('report:massBalance.table.balanceItems.materialOutput')}
            items={materialOutput}
            onEdit={initEditProductOutput}
          />
          <TableRow>
            <TableCell />
            <TableCell />
            <TableCell />
            <TableCell />
            <TableCell className="font-bold">{formattedBalance}</TableCell>
            <TableCell />
          </TableRow>
        </tbody>
      </Table>
      <div className="flex justify-end gap-2 pb-4">
        <Button
          disabled={isViewMode}
          variant="subtle"
          sizeType="sm"
          onClick={() => onAddGeneric('scrapInput')}
        >
          + {t('report:massBalance.table.balanceItems.scrapInput')}
        </Button>
        <Button
          disabled={isViewMode}
          variant="subtle"
          sizeType="sm"
          onClick={() => onAddGeneric('scrapOutput')}
        >
          + {t('report:massBalance.table.balanceItems.scrapOutput')}
        </Button>
        <Button disabled={isViewMode} variant="subtle" sizeType="sm" onClick={openCombustionDialog}>
          + {t('report:massBalance.table.balanceItems.fuelInput')}
        </Button>
        <Button
          disabled={isViewMode}
          variant="subtle"
          sizeType="sm"
          onClick={() => onAddGeneric('otherMaterialsInput')}
        >
          + {t('report:massBalance.table.balanceItems.otherMaterialsInput')}
        </Button>
        <Button
          disabled={isViewMode}
          variant="subtle"
          sizeType="sm"
          onClick={() => onAddGeneric('otherMaterialsOutput')}
        >
          + {t('report:massBalance.table.balanceItems.otherMaterialsOutput')}
        </Button>
      </div>
      <CombustionCalculatorPanel
        isOpen={isCombustionDialogOpen}
        close={() => {
          closeCombustionDialog()
          setEditedFuelInputIndex(null)
        }}
        submit={onAddCombustion}
        initialValues={editedFuelInputItem?.artifacts}
      />
      <GenericMassBalanceItemPanel
        isOpen={isMassBalanceDialogOpen}
        close={() => {
          closeMassBalanceDialog()
          setEditedBucket(null)
          setEditedItemIndex(null)
        }}
        initialValues={editedItem}
        add={onFinishEditing}
        title={t(`report:massBalance.table.balanceItems.${editedBucket}`)}
      />
      <EditMaterialOutputDialog
        isOpen={isEditMaterialOutputDialogOpen}
        close={resetEditedMaterialOutputItem}
        cnCode={editedCnCode || ''}
        initialValues={{
          quantity: editedMaterialOutputItem?.carbonContent.quantity || 0,
          carbonContent: editedMaterialOutputItem?.carbonContent.carbonContent || 0,
          emission: editedMaterialOutputItem?.carbonContent.emission || 0
        }}
        save={onEditMaterialOutput}
      />
    </>
  )
}
